/* LOGIXA homepage content — EN. Shape must match de.ts exactly. */

export const en = {
  nav: {
    links: [
      { label: 'Product', href: '#plattform' },
      { label: 'Solutions', href: '#disposition' },
      { label: 'Integrations', href: '#integrationen' },
      { label: 'Pricing', href: '#preise' },
      { label: 'Resources', href: '/docs' },
    ],
    login: 'Login',
    demo: 'Book a demo',
    menu: 'Menu',
  },

  hero: {
    kicker: 'TRANSPORT MANAGEMENT SYSTEM',
    h1: 'TMS software for hauliers who want more than dispatching.',
    sub: 'Orders, dispatch, drivers, fleet, GPS, documents and invoicing in one platform. LOGIXA connects your entire transport operation – from the order to the invoice.',
    primary: 'Book a demo',
    secondary: 'Watch live demo',
    trust: ['GDPR-compliant', 'Hosted in Germany', '9 languages', 'Live in 12 days'],
    demoNote: 'Demonstration data',
  },

  mock: {
    search: 'Search…',
    user: 'Max Mustermann',
    role: 'ADMIN',
    nav: ['Overview', 'Orders', 'Dispatch', 'Vehicles', 'Drivers', 'Route planning', 'GPS tracking', 'Documents', 'Messages', 'Reports', 'Invoicing', 'Settings'],
    live: 'LIVE',
    card: {
      id: 'FLEET-DEMO-118',
      route: 'Hamburg, DE → Rotterdam, NL',
      etaLbl: 'ETA',
      eta: 'Today, 14:30',
      driverLbl: 'DRIVER',
      driver: 'John D.',
      progress: 64,
    },
    ordersTitle: 'Active orders',
    orders: [
      { id: 'FLEET-DEMO-118', state: 'IN TRANSIT', tone: 'go' },
      { id: 'FLEET-DEMO-117', state: 'EN ROUTE', tone: 'go' },
      { id: 'FLEET-DEMO-116', state: 'LOADING', tone: 'load' },
      { id: 'FLEET-DEMO-115', state: 'PLANNED', tone: 'plan' },
      { id: 'FLEET-DEMO-114', state: 'COMPLETED', tone: 'done' },
    ],
    kpiTitle: 'KPI today',
    kpis: [
      { lbl: 'ORDERS', val: '850', delta: '+14 %' },
      { lbl: 'EMPTY RUNS', val: '14 %', delta: '−16 %' },
      { lbl: 'REVENUE', val: '€92K', delta: '+18 %' },
    ],
    statusTitle: 'Vehicle status',
    status: [
      { n: '104', lbl: 'IN TRANSIT' },
      { n: '18', lbl: 'LOADING' },
      { n: '7', lbl: 'UNLOADING' },
      { n: '4', lbl: 'SERVICE' },
      { n: '2', lbl: 'INACTIVE' },
    ],
    plates: [
      { p: 'HH-LOG 33', tone: 'go' },
      { p: 'B-KT 155', tone: 'acc' },
      { p: 'K-AB 147', tone: 'idle' },
    ],
  },

  integrations: {
    label: 'Integrated into your existing\nlogistics infrastructure',
    items: ['TIMOCOM', 'Trans.eu', 'DATEV', 'Stripe', 'SEPA', 'n8n', 'Make', 'Zapier'],
  },

  platform: {
    kicker: 'PLATFORM',
    h2: 'Your haulage business. One system.',
    sub: 'No spreadsheets. No separate driver, GPS and accounting systems. No hunting for information across five tools.',
    areas: [
      {
        n: '01',
        title: 'Orders & dispatch',
        desc: 'Capture orders, assign drivers and vehicles, plan tours and follow transport status in real time.',
        chips: ['Orders', 'AI dispatch', 'Route planning', 'ePOD'],
      },
      {
        n: '02',
        title: 'Fleet & GPS',
        desc: 'Track vehicles live, schedule maintenance, monitor TCO and manage geofences.',
        chips: ['Live GPS', 'TCO', 'Maintenance', 'Geofencing'],
      },
      {
        n: '03',
        title: 'Drivers & compliance',
        desc: 'Monitor working hours, documents, tachograph and EU 561 rules in one place – before deadlines become a problem.',
        chips: ['Tachograph', 'EU 561', 'ADR', 'Module 95'],
      },
      {
        n: '04',
        title: 'Payroll & finance',
        desc: 'Connect wages, allowances, payouts and DATEV processes directly to your operational transport data.',
        chips: ['Payroll', 'DATEV', 'SEPA', 'Stripe'],
      },
    ],
    cta: 'Explore all features',
  },

  dispatch: {
    kicker: 'INTELLIGENT DISPATCH',
    h2: 'Dispatch that thinks along.',
    sub: 'LOGIXA takes drivers, vehicles, driving times, availability and the route into account and supports dispatchers in finding the best assignment.',
    points: [
      { title: 'AI dispatch', desc: 'Suggest the driver and vehicle that fit the order.' },
      { title: 'Route optimisation', desc: 'Account for tolls, distance, driving times and fuel.' },
      { title: 'Diesel Intelligence', desc: 'Plan fuel stops from current fuel prices along the route.' },
    ],
    panelTitle: 'Route recommendation',
    metrics: [
      { lbl: 'Distance', val: '1,346 km' },
      { lbl: 'Driving time', val: '17 h 25 min' },
      { lbl: 'Toll', val: '€ 128.40' },
      { lbl: 'CO₂', val: '1,245 kg' },
    ],
    stopsTitle: 'Recommended fuel stops',
    live: 'LIVE',
    stops: [
      { city: 'Hannover', price: '€ 1.59', best: false },
      { city: 'Kassel', price: '€ 1.58', best: false },
      { city: 'Frankfurt', price: '€ 1.55', best: true },
      { city: 'Würzburg', price: '€ 1.62', best: false },
    ],
    apply: 'Apply route',
    source: 'Price sources: Tankerkönig (DE), prix-carburants (FR), E-Control (AT), MITECO (ES)',
  },

  driver: {
    kicker: 'DRIVER APP',
    h2: 'The driver has everything on board.',
    sub: 'Native apps for iOS and Android – the complete tour flow from order to signature, even without a connection.',
    flow: ['Receive order', 'Start navigation', 'Scan documents', 'Sign ePOD', 'Close tour'],
    chips: ['iOS', 'Android', 'Offline mode', 'Telegram bot', '9 languages'],
    phones: [
      {
        tag: 'ORDER #DE-1107',
        title: 'Hamburg → Rotterdam',
        rows: [
          { lbl: 'PICKUP', val: 'Today 08:30' },
          { lbl: 'DELIVERY', val: 'Today 14:30' },
          { lbl: 'VEHICLE', val: 'HH-LOG 33' },
          { lbl: 'LOAD', val: '18 pallets · 12.4 t' },
        ],
        action: 'Start navigation',
      },
      {
        tag: 'DOCUMENTS',
        title: 'Scan CMR',
        rows: [
          { lbl: 'WAYBILL', val: 'CMR-2026-4471' },
          { lbl: 'STATUS', val: 'Scanned · 2 pages' },
          { lbl: 'DELIVERY NOTE', val: 'Pending' },
        ],
        action: 'Upload scan',
      },
      {
        tag: 'ePOD',
        title: 'Confirm delivery',
        rows: [
          { lbl: 'CONSIGNEE', val: 'De Vries Logistics BV' },
          { lbl: 'SIGNATURE', val: 'Captured' },
          { lbl: 'PHOTOS', val: '3 attachments' },
        ],
        action: 'Close tour',
      },
    ],
  },

  compliance: {
    kicker: 'COMPLIANCE & SECURITY',
    h2: 'See driving times before they become a problem.',
    sub: 'Driving and rest times, tachograph data and expiring documents in one place – with a warning while there is still time to react.',
    points: [
      { title: 'EU 561', desc: 'Current driving and rest times per driver, continuously reconciled.' },
      { title: 'Tachograph', desc: 'Import DDD files and keep driver data in view.' },
      { title: 'Documents', desc: 'Warnings before licence, ADR and Module 95 expire.' },
    ],
    dashDriverLbl: 'Driver',
    dashDriver: 'John D.',
    badge: 'COMPLIANT',
    bars: [
      { lbl: 'Driving time today', val: '7 h 45 / 9 h', pct: 86, tone: 'warn' },
      { lbl: 'Driving time week', val: '38 h 10 / 56 h', pct: 68, tone: 'ok' },
      { lbl: 'Next rest period', val: 'in 1 h 15 min', pct: 42, tone: 'ok' },
    ],
    docsTitle: 'Expiring documents',
    docs: [
      { lbl: 'Driving licence', val: '12.03.2027', warn: false },
      { lbl: 'ADR certificate', val: '18.05.2027', warn: true },
      { lbl: 'Module 95', val: '22.09.2027', warn: false },
    ],
    alerts: '3 ACTIVE WARNINGS',
  },

  onboarding: {
    kicker: 'MIGRATION',
    h2: 'Still on Excel? No problem.',
    sub: 'We migrate drivers, vehicles and business partners from Excel, CSV or your current TMS.',
    promoTitle: 'Live in 12 days.',
    promoSub: 'Fast. Safe. Without risk.',
    promoCta: 'Book a demo',
    steps: [
      { tag: 'DAY 1', title: 'Review processes', desc: 'Demo and analysis of your workflows.' },
      { tag: 'DAY 2–4', title: 'Start the pilot', desc: 'Pilot operation with real vehicles.' },
      { tag: 'DAY 5–8', title: 'Import data', desc: 'Migrate drivers, vehicles and master data.' },
      { tag: 'DAY 9–10', title: 'Train the team', desc: 'Training for your dispatchers and drivers.' },
      { tag: 'DAY 11–12', title: 'Go live', desc: 'Start in production with full support.' },
    ],
  },

  roi: {
    kicker: 'SAVINGS POTENTIAL',
    h2: 'What LOGIXA moves in your fleet.',
    fields: [
      { lbl: 'Vehicles', unit: '' },
      { lbl: 'Drivers', unit: '' },
      { lbl: 'Orders per month', unit: '' },
      { lbl: 'Avg. km per vehicle and year', unit: 'km' },
    ],
    panelTitle: 'POTENTIAL PER YEAR',
    lines: ['Less administrative effort', 'Optimised empty runs', 'Fuel optimisation'],
    totalLbl: 'Estimated potential',
    basisLink: 'Show calculation basis',
    basisClose: 'Hide calculation basis',
    basis: [
      'Administration: €7.50 per order (roughly 13 minutes at a staff cost rate of €34/h) plus €15 per driver and month for time tracking and payroll preparation.',
      'Empty runs: 18 % empty kilometre share, of which 5 % avoidable through better dispatching, at €1.04 cost per kilometre.',
      'Fuel: 30 l consumption per 100 km and 2 ct/l saved by planning fuel stops along the route – based on live prices from Tankerkönig (DE), prix-carburants (FR), E-Control (AT) and MITECO (ES).',
      'All figures are an estimate based on common industry assumptions and not a guaranteed saving.',
    ],
  },

  pricing: {
    kicker: 'PRICING',
    h2: 'Transparent pricing. Predictable. Fair.',
    sub: 'Choose the plan that fits your fleet. Cancellable monthly.',
    allLink: 'See all prices',
    perMonth: '/ month',
    badge: 'RECOMMENDED',
    cta: 'Book a demo',
    plans: [
      {
        name: 'STARTER', price: '€199', custom: false, scope: 'up to 15 vehicles',
        features: ['Order management', 'Driver app', 'GPS tracking', 'Email support'],
      },
      {
        name: 'BUSINESS', price: '€499', custom: false, scope: 'up to 50 vehicles',
        features: ['All Starter features', 'AI dispatch', 'Tachograph & EU 561', 'Priority support'],
      },
      {
        name: 'ENTERPRISE', price: 'Individual', custom: true, scope: '50+ vehicles',
        features: ['All Business features', 'Advanced permissions', 'API & integrations', 'Dedicated contact'],
      },
    ],
    note: ['Smaller fleets start cheaper: ', 'BASIC from €49/month', ' for up to 3 vehicles, ', 'DRIVER free of charge', ' for owner-drivers with one truck.'],
  },

  security: {
    title: 'Your data stays in Europe.',
    items: ['Hosted in Germany', 'GDPR-compliant', '2FA & passkeys', 'Audit logs', 'Encryption at rest'],
  },

  faq: {
    kicker: 'FAQ',
    h2: 'Common questions about TMS software.',
    sub: 'Anything still open? We answer it in the demo.',
    list: [
      {
        q: 'What is TMS software?',
        a: 'A transport management system bundles order handling, dispatch, vehicle and driver management, documents and invoicing in one platform – instead of separate spreadsheets, GPS portals and accounting tools.',
      },
      {
        q: 'Which hauliers is LOGIXA suited for?',
        a: 'For hauliers and transport companies from 3 to more than 50 vehicles. Owner-drivers with one truck start on the free DRIVER plan, small fleets on BASIC from €49/month, mid-sized operators on STARTER or BUSINESS.',
      },
      {
        q: 'Can LOGIXA replace Excel?',
        a: 'Yes. Orders, drivers, vehicles, master data and invoicing live in one database instead of scattered spreadsheets. We migrate your existing Excel and CSV files – you start with your own data.',
      },
      {
        q: 'How long does implementation take?',
        a: 'We plan the migration in 12 days: process analysis, pilot operation with real vehicles, data import, training and go-live. That schedule is our method, not an average measured across customer projects.',
      },
      {
        q: 'Can I keep my existing GPS?',
        a: 'In many cases yes. LOGIXA captures positions through the driver app without extra hardware and can additionally take over telematics data from existing systems. We clarify which connection is possible in your case before the start.',
      },
      {
        q: 'Does LOGIXA support tachograph and EU 561?',
        a: 'Driving and rest times under EU 561 are evaluated continuously per driver, including warnings before limits are exceeded. DDD files from the tachograph can be imported and reviewed; the evaluation is not intended as an authoritative tachograph analysis for authorities.',
      },
      {
        q: 'Is there a driver app?',
        a: 'Yes, native apps for iOS and Android in nine languages: order, navigation, document scan, ePOD signature and tour completion – offline as well. There is also a Telegram bot for drivers without an installed app.',
      },
      {
        q: 'Can LOGIXA connect to TIMOCOM and Trans.eu?',
        a: 'The connectors are prepared and built against the documented interfaces. Activation runs through your own TIMOCOM or Trans.eu contract; during onboarding we set the connection up together and test it with your credentials.',
      },
      {
        q: 'Where is the data stored?',
        a: 'On servers in Germany, GDPR-compliant, encrypted in transit and at rest. Access is logged and logins can be secured with 2FA and passkeys.',
      },
      {
        q: 'What does LOGIXA cost?',
        a: 'DRIVER is free for owner-drivers with one truck, BASIC starts at €49/month for up to 3 vehicles, STARTER costs €199/month up to 15 vehicles, BUSINESS €499/month up to 50 vehicles. Above 50 vehicles we quote individually. All plans are cancellable monthly.',
      },
    ],
  },

  finalCta: {
    h2: 'See LOGIXA with your own transport processes.',
    sub: 'In a 30-minute demo we show you exactly the modules that matter for your fleet.',
    primary: 'Book a demo',
    secondary: 'Watch live demo',
  },

  footer: {
    tagline: 'TMS software for hauliers and transport companies in Europe.',
    cols: [
      {
        title: 'PRODUCT',
        links: [
          { label: 'Features', href: '/platform/funktionen' },
          { label: 'AI dispatch', href: '/platform/ki' },
          { label: 'Driver app', href: '/platform/funktionen#apps' },
          { label: 'Route planning', href: '/platform/funktionen#tourenplanung' },
          { label: 'Invoicing', href: '/platform/backoffice' },
        ],
      },
      {
        title: 'SOLUTIONS',
        links: [
          { label: 'Small hauliers', href: '/pricing' },
          { label: 'Mid-sized fleets', href: '/pricing' },
          { label: 'Long-haul', href: '/platform/funktionen' },
          { label: 'Owner-drivers', href: '/pricing' },
        ],
      },
      {
        title: 'INTEGRATIONS',
        links: [
          { label: 'TIMOCOM', href: '/platform/integrationen' },
          { label: 'Trans.eu', href: '/platform/integrationen' },
          { label: 'DATEV', href: '/platform/integrationen' },
          { label: 'Telegram', href: '/platform/integrationen' },
          { label: 'All integrations', href: '/platform/integrationen' },
        ],
      },
      {
        title: 'RESOURCES',
        links: [
          { label: 'Knowledge', href: '/docs' },
          { label: 'Blog', href: '/blog' },
          { label: 'What is a TMS?', href: '/docs' },
          { label: 'TMS costs', href: '/pricing' },
          { label: 'Help centre', href: '/docs' },
        ],
      },
      {
        title: 'COMPANY',
        links: [
          { label: 'About us', href: '/about' },
          { label: 'Careers', href: '/jobs' },
          { label: 'Partners', href: '/companies' },
          { label: 'Contact', href: '/kontakt' },
        ],
      },
    ],
    copyright: '© 2026 LOGIXA. All rights reserved.',
    legal: [
      { label: 'Privacy', href: '/datenschutz' },
      { label: 'Imprint', href: '/impressum' },
      { label: 'Terms', href: '/agb' },
      { label: 'Security', href: '/platform/funktionen#sicherheit' },
    ],
  },
};
